// src/Components/ControlForm.jsx
import React, { useEffect, useMemo, useState } from 'react';
import '../css/ControlForm.css';

function parseJwt(t) {
  try {
    const b64 = t.split('.')[1];
    if (!b64) return null;
    const json = decodeURIComponent(
      atob(b64.replace(/-/g, '+').replace(/_/g, '/'))
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export default function ControlForm({
  payload,
  onChange,
  onSend,
  onBluetoothReset,   // 👈 추가
  isAdmin: isAdminProp,
}) {
  const [isAdmin, setIsAdmin] = useState(Boolean(isAdminProp));

  // 부모가 isAdmin을 주면 우선 사용. 없으면 accessToken에서 추론.
  useEffect(() => {
    if (typeof isAdminProp === 'boolean') { setIsAdmin(isAdminProp); return; }
    const token = localStorage.getItem('accessToken');
    if (!token) { setIsAdmin(false); return; }
    const p = parseJwt(token);
    if (!p) { setIsAdmin(false); return; }

    const candidates = [
      p.roles,
      p.role,
      p.authorities,
      p.scopes,
      typeof p.scope === 'string' ? p.scope.split(' ') : p.scope,
    ].filter(Boolean);

    const flat = Array.isArray(candidates[0]) ? candidates.flat() : ([]).concat(...candidates);
    setIsAdmin(flat.some(r => String(r).toLowerCase().includes('admin')));
  }, [isAdminProp]);

  const radioOptions = useMemo(() => ({
    modeDirection: ['straight', 'left', 'right', 'both'],
    way: ['half', 'full'],
    angle: ['30', '60', '70', '80'],
    manual: ['on', 'off'],
  }), []);

  const labelMap = useMemo(() => ({
    modeDirection: '피난 방향 지시',
    way: '스캔 모드',
    angle: '스캔 각도',
    manual: '경보 상태',
  }), []);

  const safeOnChange = (next) => {
    if (!isAdmin) { alert('관리자만 설정을 변경할 수 있습니다.'); return; }
    onChange?.(next);
  };

  const safeOnSend = () => {
    if (!isAdmin) { alert('관리자만 제어 명령을 전송할 수 있습니다.'); return; }
    onSend?.();
  };

  const safeOnBluetoothReset = () => {       // 👈 추가
    if (!isAdmin) { alert('관리자만 블루투스 모드 전환을 할 수 있습니다.'); return; }
    onBluetoothReset?.();
  };

  if (!payload) return null;

  return (
    <div className={`control-form ${!isAdmin ? 'control-form--readonly' : ''}`}>
      {!isAdmin && (
        <div className="admin-guard-banner" role="note">
          🔒 관리자 전용: 이 화면은 조회만 가능합니다.
        </div>
      )}

      <div className="control-table-header">
        <div className="control-header-cell">항목명</div>
        <div className="control-header-cell">설정값</div>
      </div>

      {Object.keys(payload)
        .filter((key) => key !== 'id' && key !== 'modechange')
        .map((key) => {
          const options = radioOptions[key];
          const labelText = labelMap[key] || key;
          const containerClass = [
            'control-radio-options',
            key === 'angle' && 'control-radio-options--angle',
            key === 'modeDirection' && 'control-radio-options--dir',
            key === 'way' && 'control-radio-options--way',
            key === 'manual' && 'control-radio-options--manual',
          ].filter(Boolean).join(' ');

          return (
            <div key={key} className="control-radio-row">
              <div className="control-label">{labelText}</div>
              <div className={containerClass} aria-disabled={!isAdmin}>
                {key === 'manual' && Array.isArray(options)
                  ? options.map((option) => {
                      const id = `${key}-${option}`;
                      const checked = payload[key] === option;
                      return (
                        <label
                          key={option}
                          htmlFor={id}
                          className={`toggle-wrapper ${!isAdmin ? 'is-disabled' : ''}`}
                          title={!isAdmin ? '관리자 전용' : undefined}
                        >
                          <input
                            id={id}
                            type="radio"
                            name={key}
                            value={option}
                            className="toggle-radio"
                            checked={checked}
                            onChange={() => safeOnChange({ ...payload, [key]: option })}
                            disabled={!isAdmin}
                          />
                          <span className="toggle-label">{option.toUpperCase()}</span>
                        </label>
                      );
                    })
                  : Array.isArray(options)
                  ? options.map((option) => {
                      const id = `${key}-${option}`;
                      const checked = payload[key] === option;
                      return (
                        <label
                          key={option}
                          htmlFor={id}
                          className={`pill-label ${!isAdmin ? 'is-disabled' : ''}`}
                          title={!isAdmin ? '관리자 전용' : undefined}
                        >
                          <input
                            id={id}
                            type="radio"
                            name={key}
                            value={option}
                            className="pill-radio"
                            checked={checked}
                            onChange={() => safeOnChange({ ...payload, [key]: option })}
                            disabled={!isAdmin}
                          />
                          <span className={`pill-text ${checked ? 'pill-text--active' : ''}`}>{option}</span>
                        </label>
                      );
                    })
                  : (
                    <div className="readonly-value">{String(payload[key] ?? '')}</div>
                  )}
              </div>
            </div>
          );
        })}

      {/* ✅ 제어 버튼 + 블루투스 버튼 나란히 */}
      <div className="control-actions">
        <button
          onClick={safeOnSend}
          className="control-button"
          disabled={!isAdmin}
          title={!isAdmin ? '관리자 전용' : undefined}
        >
          제어 명령 전송
        </button>

        {onBluetoothReset && (
          <button
            type="button"
            onClick={safeOnBluetoothReset}
            className="control-button control-button--secondary"
            disabled={!isAdmin}
            title={!isAdmin ? '관리자 전용' : undefined}
          >
            블루투스 모드 전환
          </button>
        )}
      </div>
    </div>
  );
}
